import { useCallback } from 'react';
import type { DownloadSize } from '@photos/shared';
import { requestDownloadUrls } from '../api/hooks';
import { BROWSER_ZIP_LIMIT, chooseSaveTarget, streamZip, triggerUrlDownload } from '../lib/download';
import { fmtSize } from '../lib/format';
import { useToast } from '../state/toasts';

const MB = 1024 * 1024;

export const SIZE_LABEL: Record<DownloadSize, string> = {
  original: 'Originals',
  large: 'Large 2048px',
  social: 'Social 1080px',
};

/** Estimated ZIP size before asking the server (prototype estimates for web sizes). */
export function estimateBytes(photos: { bytes: number }[], size: DownloadSize): number {
  if (size === 'original') return photos.reduce((a, p) => a + p.bytes, 0);
  return photos.length * (size === 'large' ? 0.9 : 0.35) * MB;
}

export function useDownloads() {
  const toast = useToast();

  /** Bulk download as a ZIP streamed in the browser. Call directly from a click handler (save picker needs it). */
  const downloadZip = useCallback(
    async (photos: { id: string; bytes: number }[], size: DownloadSize) => {
      const n = photos.length;
      const estimate = estimateBytes(photos, size);
      if (estimate > BROWSER_ZIP_LIMIT) {
        toast({
          icon: 'dl',
          title: 'Too large to zip in your browser',
          sub: `ZIPs are made in your browser, up to 2 GB at a time. This selection is about ${(estimate / 1024 ** 3).toFixed(1)} GB — select fewer photos (for example one day or one tag) and download in smaller batches.`,
        });
        return;
      }
      const target = await chooseSaveTarget(`JBCH-photos-${n}.zip`);
      if (!target) return;
      const t = toast({
        icon: 'dl',
        title: `Zipping ${n} photos`,
        sub: `${SIZE_LABEL[size]} · streaming in your browser (no server cost)`,
        progress: 0,
      });
      try {
        const { files } = await requestDownloadUrls(
          photos.map((p) => p.id),
          size,
        );
        const total = files.reduce((a, f) => a + f.bytes, 0) || estimate;
        let last = 0;
        await streamZip(files, target, (loaded) => {
          const now = performance.now();
          if (now - last < 120) return;
          last = now;
          t.update({ progress: Math.min(0.99, loaded / total) });
        });
        t.update({ progress: 1, sub: `Saved ${target.name} · ${fmtSize(total)}` });
        t.close(3500);
      } catch (e) {
        t.update({ progress: undefined, title: 'Download failed', sub: (e as Error).message });
        t.close(6000);
      }
    },
    [toast],
  );

  /** Single photo: a direct download from a short-lived signed URL. */
  const downloadOne = useCallback(
    async (id: string, size: DownloadSize) => {
      try {
        const { files } = await requestDownloadUrls([id], size);
        const f = files[0];
        if (!f) throw new Error('Nothing to download.');
        triggerUrlDownload(f.url, f.name);
        toast({
          icon: 'dl',
          title: 'Download started',
          sub: `${size === 'original' ? 'Original' : SIZE_LABEL[size]} via a 5-minute signed link.`,
        });
      } catch (e) {
        toast({ icon: 'info', title: 'Download failed', sub: (e as Error).message });
      }
    },
    [toast],
  );

  return { downloadZip, downloadOne };
}
